import React, { memo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import {
  Button,
  Heading,
  PageLoader,
  VirtualTable,
  VirtualTableHeaderItem,
  VirtualTableItem,
} from '../../npaper';
import { Container, PageContainer } from '../../npaper/PageContainer';
import { IProductGroup, ProductGroup, useStores } from '../../stores';
import { SearchInput } from '../../npaper/SearchInput';
import { Logo } from '../Logo';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import ProductGroupDetailsDialog from './ProductGroupDetailsDialog';
import { useHistory } from 'react-router-dom';

const ProductGroups = () => {
  const { productGroupStore } = useStores();
  const intl = useIntl();
  const history = useHistory();
  const [isCreating, setIsCreating] = useState(false);

  const { items, isLoading, fetchMore, total, setFilter, filter } =
    useFilterFetch<typeof ProductGroup>(productGroupStore as any, {
      filter: {},
      ordering: 'name',
    });

  const header: VirtualTableHeaderItem[] = [
    {
      title: intl.formatMessage({ defaultMessage: 'Name' }),
      orderByKeys: ['name', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Description' }),
      orderByKeys: ['description', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Default CMR' }),
      orderByKeys: ['default_cmr', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Products in group' }),
      orderByKeys: ['products__count', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Pricing matrix' }),
      orderByKeys: ['pricing__count', 'id'],
    },
  ];

  return !items || total === undefined ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom={total === 0 ? undefined : 0}
      justifyContent={total === 0 ? 'center' : 'flex-start'}
      height="100%"
      maxHeight="100vh"
    >
      <ProductGroupDetailsDialog
        isShown={isCreating}
        onClose={(group) => {
          setIsCreating(false);
        }}
      />
      {total === 0 ? (
        <div className="text-center max-w-xs mx-auto">
          <Logo className="mx-auto" />
          <Heading size={500} className="mt-2">
            <FormattedMessage defaultMessage="Create a new group" />
          </Heading>
          <p className="my-1">
            <FormattedMessage defaultMessage="Group your products and manage the right pricing for your products." />
          </p>
          <Button
            iconLeft="add"
            display="inline-flex"
            onClick={() => setIsCreating(true)}
          >
            <FormattedMessage defaultMessage="Create new group" />
          </Button>
        </div>
      ) : (
        <>
          <Container className="mb-2 flex-shrink-0 flex-grow-0">
            <Heading size={700} className="mb-3">
              <FormattedMessage defaultMessage="Product groups" />
            </Heading>
            <div className="flex justify-between">
              <SearchInput
                className="mr-1"
                currentQuery={filter.filter ? filter.filter.search : ''}
                onSearch={(query) => {
                  setFilter({
                    ...filter,
                    filter: { ...filter.filter, search: query },
                  });
                }}
              />
              <Button
                iconLeft="add"
                marginLeft="auto"
                onClick={() => setIsCreating(true)}
              >
                <FormattedMessage defaultMessage="Create new group" />
              </Button>
            </div>
          </Container>
          <VirtualTable
            isLoading={isLoading}
            items={items}
            total={total}
            ordering={filter.ordering}
            onOrderingChange={(ordering) =>
              setFilter({ filter: filter.filter, ordering })
            }
            header={header}
            footer={false}
            onRequestMore={async () => fetchMore()}
            renderRow={({ item }) => <MemoizedRow group={item} />}
            onSelect={(indices) => {
              const selectedIndex = Object.keys(indices)
                .filter((key) => indices[Number(key)])
                .pop();
              if (selectedIndex) {
                history.push(
                  `/product-groups/${items[Number(selectedIndex)].id}`
                );
              }
            }}
          />
        </>
      )}
    </PageContainer>
  );
};

const MemoizedRow = memo(({ group }: { group: IProductGroup }) => {
  return (
    <>
      <VirtualTableItem>
        <span className="medium">({group.id})</span>
        <span className="ml-0.5">{group.name}</span>
      </VirtualTableItem>
      <VirtualTableItem>{group.description}</VirtualTableItem>
      <VirtualTableItem justifyContent="flex-end">
        {/* eslint-disable-next-line react/style-prop-object */}
        <FormattedNumber style="percent" value={group.default_cmr} />
      </VirtualTableItem>
      <VirtualTableItem justifyContent="flex-end">
        {group.products__count}
      </VirtualTableItem>
      <VirtualTableItem justifyContent="flex-end">
        {group.pricing.length}
      </VirtualTableItem>
    </>
  );
});

export default ProductGroups;
